import {
  Copy,
  MoreHorizontal,
  Pencil,
  Save,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import type { JSX, MouseEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MarkdownEditor from "@/components/ui/EditorMarkdown";
import { NoteFeedbackPrompt } from "@/components/ui/feedback/NoteFeedbackPrompt";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ToastAction } from "@/components/ui/toast";
import { V3Button } from "@/components/ui/v3/button";
import { toast } from "@/hooks/use-toast";
import {
  type CaretTarget,
  caretTargetAt,
  caretTargetFromPosition,
} from "@/lib/clickCaret";
import { trackOnboarding } from "@/lib/guided/events";
import { editNoteUnit } from "@/lib/noteSentences";
import {
  caretAtPoint,
  type SentenceHit,
  sentenceAtCaret,
} from "@/lib/sentenceAtPoint";
import { cn } from "@/lib/utils";
import { acceptAllVerifyFlags } from "@/lib/verifyFlags";
import { countVerifyMarkers } from "@/lib/verifyMarkers";
import { completeChecklistItem } from "@/store/guidedChecklist.store";
import { copyNoteToClipboard } from "../components/copyNote";
import { SectionedNote } from "../components/SectionedNote";
import { usePhoneLayout } from "./layout";
import { ReviewCard } from "./ReviewCard";
import { NoteSourcesLayer, type SourcePoint } from "./review/NoteSourcesLayer";
import { SentenceEditor } from "./SentenceEditor";
import { AcceptAllSheet } from "./UnreviewedSheet";
import {
  useVerifyReview,
  type VerifyGatedAction,
  VerifyGateProvider,
} from "./VerifyReview";
import { useRegisterDocumentControls } from "./v3/DocumentControls";

// Asks the document to run an action through its review step (creating a
// patient summary or referral letter from the main note). `key` changes per
// request.
export interface VerifyReviewRequest {
  key: number;
  action: VerifyGatedAction;
}

// Larger, calmer reading size for the note: 16px on phones, 17px with a
// longer line height from `sm` up. The markdown stylesheet sets 1rem on
// every block, so this targets those blocks directly.
// Click-to-edit (v3.2): a click on the note's text opens a small editor
// with only that sentence (SentenceEditor; the full editor when the click
// cannot be placed), except on its controls and links ([verify] values,
// section copy, links), while text is selected, or on the second click of a
// double click (selecting a word). The wait lets a double click win over
// the single click.
const CLICK_TO_EDIT_SKIP =
  "a, button, input, textarea, select, [role=button], [data-verify-index], [contenteditable=true]";
export const CLICK_TO_EDIT_DELAY_MS = 250;
const hasTextSelection = (): boolean =>
  !!window.getSelection()?.toString().trim();

export const NOTE_READING_CLASS =
  "max-w-[72ch] [&_.editor-markdown>*]:text-base [&_.editor-markdown>*]:leading-[1.65] sm:[&_.editor-markdown>*]:text-[17px]";

interface Props {
  // Document name and details, shown left of Copy.
  heading?: React.ReactNode;
  result: string | null | undefined;
  saving?: boolean;
  // Busy with another action (e.g. regenerating): blocks Edit and Save.
  busy?: boolean;
  editable?: boolean;
  // Resolves true when saved: the editor then closes on the saved note. On
  // false (or an error) it stays open with the draft; the failure is a toast.
  // `quiet`: no success toast (the review card shows the next value instead).
  onSave?: (value: string, options?: { quiet?: boolean }) => Promise<boolean>;
  onEditingChange?: (editing: boolean) => void;
  // Opens the assistant (drawer or sheet on touch, or a collapsed panel).
  onOpenAssistant?: () => void;
  // Main note only: Regenerate and the version picker.
  tools?: React.ReactNode;
  // Phones: the same tools as items of the "…" menu, and the notices that
  // otherwise sit with them (e.g. transcription recovery).
  menuItems?: React.ReactNode;
  notices?: React.ReactNode;
  // Export (PDF, Word) sits next to Copy when note export is enabled.
  exportAction?: React.ReactNode;
  banner?: React.ReactNode;
  // In-app feedback: "Something off with this note?" under the text, with
  // this visit's id offered.
  feedbackVisitPublicId?: string;
  reviewRequest?: VerifyReviewRequest | null;
  // Called once the document has taken the request.
  onReviewRequestTaken?: () => void;
  // v3.2: the visit header has the one "Copy to chart" for this note, so the
  // document has no Copy button of its own (section copy stays).
  copyInHeader?: boolean;
  // "Sources" for each sentence of the read view (VITE_NOTE_SOURCES_ENABLED,
  // latest main note only).
  sourcesVisitPublicId?: string;
  // A pre-chart draft: its values come from the clinician, not a recording.
  reviewHint?: string;
  reviewCardWording?: { title: string; explain: string };
  // The note safety check found an allergy conflict (a red allergy flag):
  // the copy and export gate says so instead of "unclear values".
  allergyFlagged?: boolean;
  children?: React.ReactNode;
}

// "Needs review · N": values the note marked "[verify]" because the
// recording was unclear. Pressing it opens the editor at the first one.
const NeedsReviewBadge = ({
  count,
  onOpen,
  goesToNext = false,
}: {
  count: number;
  onOpen?: () => void;
  // Review card: each press goes to the next value.
  goesToNext?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const className =
    "inline-flex min-h-11 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 text-sm font-medium text-amber-950 lg:min-h-8 dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-100";
  const content = (
    <>
      <TriangleAlert aria-hidden className="size-4 text-amber-600" />
      {t("visit.workspace.verify.badge", { count })}
    </>
  );
  return onOpen ? (
    <button
      aria-label={
        goesToNext
          ? t("visit.workspace.verify.card.badgeAria", { count })
          : t("visit.workspace.verify.badgeAria", { count })
      }
      className={cn(
        className,
        "hover:bg-amber-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-amber-400/25",
      )}
      data-testid="needs-review"
      title={t("visit.workspace.verify.tooltip")}
      type="button"
      onClick={onOpen}
    >
      {content}
    </button>
  ) : (
    <span
      className={className}
      data-testid="needs-review"
      title={t("visit.workspace.verify.tooltip")}
    >
      {content}
    </span>
  );
};

// v3.2 (as iOS): "Needs review · N" as a banner above the note, with Review
// (the ink primary) opening the first flagged value.
export const NeedsReviewBanner = ({
  count,
  onReview,
  onAcceptAll,
  hint,
}: {
  count: number;
  hint?: string;
  onReview?: () => void;
  // "Accept all" (VITE_REVIEW_COPY_ANYWAY_ENABLED), after a confirmation.
  onAcceptAll?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      className="mb-6 flex items-center gap-3 rounded-[14px] bg-v3-warn-tint py-3 pl-4 pr-3 font-v3"
      data-testid="needs-review-banner"
      role="status"
    >
      <TriangleAlert
        aria-hidden
        className="size-5 shrink-0 text-v3-warn dark:text-v3-warn-text"
      />
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-v3-warn-text">
          {t("visit.workspace.verify.badge", { count })}
        </p>
        <p className="text-[13px] leading-snug text-v3-text-2">
          {hint ?? t("v3.workspace.needsReviewHint")}
        </p>
      </div>
      <div className="flex shrink-0 flex-col-reverse items-stretch gap-1.5 min-[420px]:flex-row min-[420px]:items-center">
        {onAcceptAll && (
          <V3Button
            className="text-v3-warn-text hover:bg-v3-panel/60"
            data-testid="accept-all"
            variant="ghost"
            onClick={onAcceptAll}
          >
            {t("visit.workspace.verify.bulk.acceptAll")}
          </V3Button>
        )}
        {onReview && (
          // The state's one primary while values are flagged: Copy to
          // chart steps down to a secondary until they are reviewed.
          <V3Button
            data-testid="needs-review"
            variant="primary"
            onClick={onReview}
          >
            {t("v3.workspace.review")}
          </V3Button>
        )}
      </div>
    </div>
  );
};

// Toolbar and body of one visit document (the clinical note, a referral
// letter, a patient summary...). Copy is the primary action because pasting
// into the EMR is the most frequent step after a visit.
export const NoteDocument = ({
  heading,
  result: savedResult,
  saving = false,
  busy = false,
  editable = true,
  onSave,
  onEditingChange,
  onOpenAssistant,
  tools,
  menuItems,
  notices,
  exportAction,
  banner,
  feedbackVisitPublicId,
  reviewRequest,
  onReviewRequestTaken,
  copyInHeader = false,
  sourcesVisitPublicId,
  reviewHint,
  reviewCardWording,
  allergyFlagged = false,
  children,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const compact = usePhoneLayout();
  // v3.2 (VITE_APP_REDESIGN_ENABLED): no toolbar on the note; Edit, Export
  // and the version history are in the visit header (DocumentControls).
  const v3 = AppConfig.enableAppRedesign;
  const [editing, setEditing] = useState(false);
  // Review card (VITE_REVIEW_CARD_ENABLED): the "[verify]" value it shows,
  // and the note as last saved from it until the saved note comes back.
  const reviewCard = AppConfig.enableReviewCard && !children;
  const [cardIndex, setCardIndex] = useState<number | null>(null);
  const [reviewed, setReviewed] = useState<string | null>(null);
  // Click-to-edit: the sentence open in the sentence editor.
  const [sentence, setSentence] = useState<SentenceHit | null>(null);
  const [bodyElement, setBodyElement] = useState<HTMLDivElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new note from the server replaces the local copy.
  useEffect(() => {
    setReviewed(null);
  }, [savedResult]);
  const result = reviewed ?? savedResult;
  // A sentence is placed in the note it was clicked in.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on a new note.
  useEffect(() => {
    setSentence(null);
  }, [result]);
  const resultRef = useRef(result);
  resultRef.current = result;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const [draft, setDraft] = useState(result || "");
  const hasText = !!result?.trim();
  // Which "[verify]" marker the editor should select when it opens.
  const [focusMarker, setFocusMarker] = useState<{
    index: number;
    key: number;
  } | null>(null);
  // Live while editing, so the count drops as the clinician removes markers.
  const reviewCount = countVerifyMarkers(editing ? draft : result);
  const canEdit = editable && !!onSave && !busy && hasText;

  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing, onEditingChange]);
  useEffect(
    () => (): void => {
      onEditingChange?.(false);
    },
    [onEditingChange],
  );

  // Where the click that opened the editor was (click-to-edit).
  const [focusCaret, setFocusCaret] = useState<
    (CaretTarget & { key: number }) | null
  >(null);
  const startEditing = (): void => {
    setDraft(result || "");
    setFocusMarker(null);
    setFocusCaret(null);
    setCardIndex(null);
    setSentence(null);
    setEditing(true);
  };
  const startEditingAt = (target: CaretTarget | null): void => {
    startEditing();
    if (target) setFocusCaret({ ...target, key: Date.now() });
  };

  // Click-to-edit: the pending single click (cancelled by a second click or
  // a selection made meanwhile).
  const startEditingRef = useRef(startEditingAt);
  startEditingRef.current = startEditingAt;
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClickToEdit = (): boolean => {
    if (clickTimer.current === null) return false;
    clearTimeout(clickTimer.current);
    clickTimer.current = null;
    return true;
  };
  useEffect(
    () => () => {
      if (clickTimer.current !== null) clearTimeout(clickTimer.current);
    },
    [],
  );

  const closeCard = useCallback(() => setCardIndex(null), []);
  // "Accept all" (VITE_REVIEW_COPY_ANYWAY_ENABLED, clinical lead 28/10): every
  // open value accepted as written in one save, after a confirmation.
  const [acceptAllOpen, setAcceptAllOpen] = useState(false);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const canAcceptAll =
    AppConfig.enableReviewCopyAnyway &&
    canEdit &&
    !editing &&
    reviewCount > 0 &&
    !acceptingAll;
  const askAcceptAll = canAcceptAll ? () => setAcceptAllOpen(true) : undefined;
  const acceptAll = async (): Promise<void> => {
    if (!onSave || !result) return;
    const count = countVerifyMarkers(result);
    const next = acceptAllVerifyFlags(result);
    setAcceptingAll(true);
    let saved = false;
    try {
      saved = await onSave(next);
    } catch {
      saved = false;
    }
    setAcceptingAll(false);
    setAcceptAllOpen(false);
    // A failed save keeps every value flagged; the save shows its own error.
    if (!saved) return;
    setReviewed(next);
    setCardIndex(null);
    completeChecklistItem("review");
    trackOnboarding("verify_reviewed", { via: "accept-all" });
    toast({
      variant: "success",
      title: t("visit.workspace.verify.bulk.acceptedAll", { count }),
    });
  };
  const acceptAllSheet = (
    <AcceptAllSheet
      busy={acceptingAll}
      count={reviewCount}
      open={acceptAllOpen}
      onConfirm={() => void acceptAll()}
      onOpenChange={(open) => {
        if (!acceptingAll) setAcceptAllOpen(open);
      }}
    />
  );
  // Undo of a removal (review card Remove, a sentence saved empty): the
  // note as it was is saved again, while it is still the note the removal
  // made. It lands in the version history like any save.
  const undoRemoval = async (before: string, after: string): Promise<void> => {
    const save = onSaveRef.current;
    if (!save) return;
    if (resultRef.current !== after) {
      toast({
        variant: "error",
        title: t("v3.workspace.sentence.undoStale"),
      });
      return;
    }
    let saved = false;
    try {
      saved = await save(before, { quiet: true });
    } catch {
      saved = false;
    }
    // A failed save shows its own error.
    if (!saved) return;
    setReviewed(before);
    toast({ variant: "success", title: t("v3.workspace.sentence.undone") });
  };
  const toastRemoved = (title: string, before: string, after: string): void => {
    toast({
      variant: "success",
      title,
      action: (
        <ToastAction
          altText={t("v3.workspace.sentence.undo")}
          data-testid="note-undo"
          onClick={() => void undoRemoval(before, after)}
        >
          {t("v3.workspace.sentence.undo")}
        </ToastAction>
      ),
    });
  };

  // Saves an Accept, Edit or Remove from the card, then moves on to the
  // value that took its place (or closes once none is left). A removal
  // offers Undo.
  const saveFromCard = async (
    next: string,
    action?: "accept" | "edit" | "remove",
  ): Promise<boolean> => {
    if (!onSave) return false;
    const before = result ?? "";
    const saved = await onSave(next, { quiet: true });
    if (!saved) return false;
    setReviewed(next);
    completeChecklistItem("review");
    trackOnboarding("verify_reviewed", { via: "card" });
    const left = countVerifyMarkers(next);
    if (action === "remove")
      toastRemoved(t("visit.workspace.verify.card.removed"), before, next);
    if (left === 0) {
      setCardIndex(null);
      if (action !== "remove")
        toast({
          variant: "success",
          title: t("visit.workspace.verify.card.allChecked"),
        });
    } else
      setCardIndex((index) => (index === null || index >= left ? 0 : index));
    return true;
  };

  // Saves the sentence editor's text: the note with that sentence changed,
  // through the note's normal save (a new version, as the full editor's
  // save makes). Saved empty (or its Remove), the sentence goes, a list
  // item with its whole line, and Undo is offered.
  const saveSentence = async (value: string): Promise<boolean> => {
    if (!onSave || !sentence || !result) return false;
    const before = result;
    const next = editNoteUnit(before, sentence.unit, value);
    if (next === before) return true;
    const removing = !value.trim();
    const saved = removing
      ? await onSave(next, { quiet: true })
      : await onSave(next);
    if (!saved) return false;
    setReviewed(next);
    if (removing)
      toastRemoved(
        sentence.unit.line.kind === "paragraph"
          ? t("v3.workspace.sentence.removed")
          : t("v3.workspace.sentence.lineRemoved"),
        before,
        next,
      );
    return true;
  };

  const openAtMarker = (index: number): void => {
    if (!editing) {
      setDraft(result || "");
      setEditing(true);
    }
    setFocusMarker({ index, key: Date.now() });
  };

  // "[verify]" values are resolved before the note is copied, exported or
  // used for a summary or letter (VerifyReview).
  const review = useVerifyReview({
    result,
    allergyFlagged,
    onSave: canEdit ? onSave : undefined,
    onEdit: canEdit ? openAtMarker : undefined,
  });
  const gate = useRef(review.gate);
  gate.current = review.gate;
  const taken = useRef(onReviewRequestTaken);
  taken.current = onReviewRequestTaken;
  const requestKey = reviewRequest?.key;
  const requestAction = useRef(reviewRequest?.action);
  requestAction.current = reviewRequest?.action;
  useEffect(() => {
    if (requestKey === undefined || !requestAction.current) return;
    gate.current.request(requestAction.current);
    taken.current?.();
  }, [requestKey]);
  const copyNote = (): void => {
    review.gate.request({
      reason: "copy",
      label: t("visit.workspace.copyNote"),
      proceed: (text) => void copyNoteToClipboard(text),
    });
  };

  const cardOpen = reviewCard && !editing;
  const acceptAllButton = askAcceptAll ? (
    <Button
      className="min-h-11 lg:min-h-8"
      data-testid="accept-all"
      size="sm"
      type="button"
      variant="ghost"
      onClick={askAcceptAll}
    >
      {t("visit.workspace.verify.bulk.acceptAll")}
    </Button>
  ) : null;
  const reviewBadge =
    reviewCount > 0 ? (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {cardOpen ? (
          // Each press goes to the next flagged value.
          <NeedsReviewBadge
            goesToNext
            count={reviewCount}
            onOpen={() =>
              setCardIndex((index) =>
                index === null ? 0 : (index + 1) % reviewCount,
              )
            }
          />
        ) : (
          <NeedsReviewBadge
            count={reviewCount}
            onOpen={canEdit || editing ? () => openAtMarker(0) : undefined}
          />
        )}
        {acceptAllButton}
      </div>
    ) : null;

  const save = async (): Promise<void> => {
    if (!onSave) return;
    let saved = false;
    try {
      saved = await onSave(draft);
    } catch {
      saved = false;
    }
    if (saved) setEditing(false);
  };

  const sectioned = (
    <SectionedNote
      highlightFlags={cardOpen}
      result={result || ""}
      onVerifyClick={
        cardOpen ? setCardIndex : canEdit ? openAtMarker : undefined
      }
    />
  );
  const clickToEdit = v3 && !children && !editing && canEdit;
  // Note sources on: a plain click opens the sentence's source, and the
  // source card's Edit opens the sentence editor for that sentence (the
  // full editor when it cannot be placed).
  const editFromSource = (point: SourcePoint | null): void => {
    const note = resultRef.current;
    const hit =
      point && note && bodyElement
        ? sentenceAtCaret(bodyElement, note, point)
        : null;
    if (!hit) {
      startEditingAt(
        point && bodyElement
          ? caretTargetFromPosition(bodyElement, point.node, point.offset)
          : null,
      );
      return;
    }
    setCardIndex(null);
    setSentence(hit);
  };
  const sentenceOpen = useRef(false);
  sentenceOpen.current = !!sentence;
  const onBodyClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (!clickToEdit) return;
    // A changed sentence stays open (Save, Cancel or Escape first).
    if (sentenceOpen.current) return;
    // Taken by a layer inside (a sentence's source opened).
    if (event.defaultPrevented) return;
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    if ((event.target as Element).closest(CLICK_TO_EDIT_SKIP)) return;
    if (hasTextSelection()) return;
    // The second click of a double click selects a word instead.
    if (cancelClickToEdit() || event.detail > 1) return;
    // The sentence under the click; without one, the full editor opens with
    // the cursor at the clicked sentence.
    const container = event.currentTarget;
    const caret = caretAtPoint(document, event.clientX, event.clientY);
    const target = caretTargetAt(container, event.clientX, event.clientY);
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      if (hasTextSelection()) return;
      const note = resultRef.current;
      const hit =
        caret && note ? sentenceAtCaret(container, note, caret) : null;
      if (hit) {
        setCardIndex(null);
        setSentence(hit);
      } else startEditingRef.current(target);
    }, CLICK_TO_EDIT_DELAY_MS);
  };
  const body = (
    // biome-ignore lint/a11y/useKeyWithClickEvents: a shortcut for pointer users; the Edit button on the note bar (and "…" → Edit note) is the keyboard path.
    // biome-ignore lint/a11y/noStaticElementInteractions: as above.
    <div
      ref={setBodyElement}
      className={cn(
        v3 ? "py-1" : "px-4 py-5 sm:px-6 sm:py-6 lg:px-8",
        NOTE_READING_CLASS,
        clickToEdit && "cursor-text",
      )}
      data-click-to-edit={clickToEdit ? "true" : undefined}
      data-testid="note-body"
      onClick={onBodyClick}
    >
      {children ??
        (editing ? (
          <MarkdownEditor
            focusCaret={focusCaret}
            focusVerifyMarker={focusMarker}
            initValue={result || ""}
            onChange={setDraft}
          />
        ) : sourcesVisitPublicId && hasText ? (
          // Sources: a click on a sentence; flagged values keep their own
          // click (the review card).
          <NoteSourcesLayer
            result={result || ""}
            visitPublicId={sourcesVisitPublicId}
            onEdit={clickToEdit ? editFromSource : undefined}
          >
            {sectioned}
          </NoteSourcesLayer>
        ) : (
          sectioned
        ))}
    </div>
  );

  const card =
    cardOpen && cardIndex !== null && result ? (
      <ReviewCard
        container={bodyElement}
        index={cardIndex}
        note={result}
        phone={compact}
        onClose={closeCard}
        onAcceptAll={askAcceptAll}
        onIndexChange={setCardIndex}
        onSave={canEdit ? saveFromCard : undefined}
        wording={reviewCardWording}
      />
    ) : null;

  const sentenceEditor =
    sentence && clickToEdit && !editing ? (
      <SentenceEditor
        key={`${sentence.unit.start}:${sentence.unit.end}`}
        hit={sentence}
        phone={compact}
        onClose={() => setSentence(null)}
        onEditWholeNote={() => {
          const range = sentence.range;
          const target =
            range && bodyElement
              ? caretTargetFromPosition(
                  bodyElement,
                  range.startContainer,
                  range.startOffset,
                )
              : null;
          startEditingAt(target);
        }}
        onRemove={() => saveSentence("")}
        onSave={saveSentence}
      />
    ) : null;

  const feedbackPrompt =
    feedbackVisitPublicId && hasText && !editing ? (
      <NoteFeedbackPrompt
        className={
          v3
            ? "mt-8 border-t border-v3-border pt-4"
            : "border-t px-4 py-3 sm:px-6 lg:px-8"
        }
        visitPublicId={feedbackVisitPublicId}
      />
    ) : null;

  const saveButton = (
    <Button disabled={saving || busy} type="button" onClick={() => void save()}>
      {saving ? <LoadingSpinner /> : <Save />}
      {t("common.save")}
    </Button>
  );
  const cancelButton = (
    <Button
      type="button"
      variant="outline"
      onClick={() => {
        setEditing(false);
      }}
    >
      {t("common.cancel")}
    </Button>
  );

  const openReview = cardOpen
    ? () => setCardIndex(0)
    : canEdit || editing
      ? () => openAtMarker(0)
      : undefined;
  useRegisterDocumentControls(
    v3
      ? {
          canEdit: editable && !!onSave && !busy && hasText && !editing,
          editing,
          startEditing,
          exportAction,
          menuItems,
          gate: review.gate,
          reviewCount,
          openReview,
          revision: result ?? "",
          overlay: !!card || !!sentenceEditor,
        }
      : null,
  );

  if (v3) {
    return (
      <VerifyGateProvider value={review.gate}>
        <div className="min-w-0 font-v3" data-testid="note-document">
          {heading && <div className="mb-4">{heading}</div>}
          {editing && (
            <div
              aria-label={t("visit.workspace.noteActions")}
              className="sticky top-0 z-10 -mx-2 mb-3 flex items-center gap-2 rounded-xl bg-v3-panel px-2 py-2"
              role="toolbar"
            >
              <span className="mr-auto text-[13px] font-medium text-v3-text-2">
                {t("v3.workspace.editingNote")}
              </span>
              <V3Button
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                }}
              >
                {t("common.cancel")}
              </V3Button>
              <V3Button
                data-testid="note-save"
                disabled={saving || busy}
                variant="primary"
                onClick={() => void save()}
              >
                {saving ? <LoadingSpinner /> : <Save />}
                {t("common.save")}
              </V3Button>
            </div>
          )}
          {reviewCount > 0 && !editing && (
            <NeedsReviewBanner
              count={reviewCount}
              hint={reviewHint}
              // While a card is open its own buttons are the actions (one
              // primary, no Review or Accept all twice).
              onAcceptAll={card || sentenceEditor ? undefined : askAcceptAll}
              onReview={card || sentenceEditor ? undefined : openReview}
            />
          )}
          {notices && (
            <div className="mb-4 space-y-2 text-sm empty:hidden">{notices}</div>
          )}
          {banner}
          {body}
          {feedbackPrompt}
          {(card || sentenceEditor) && compact && (
            <div aria-hidden className="h-[60dvh]" />
          )}
        </div>
        {review.dialog}
        {acceptAllSheet}
        {card}
        {sentenceEditor}
      </VerifyGateProvider>
    );
  }

  // Phones: one row under the heading (Copy, Edit, "…"), so the note starts
  // right after it. History, Regenerate and Edit with assistant are in "…".
  if (compact) {
    const hasMenu = !!menuItems || !!onOpenAssistant;
    return (
      <VerifyGateProvider value={review.gate}>
        <div className="min-w-0">
          <div className="px-4 pb-3">
            {heading}
            {reviewBadge}
          </div>
          <div
            aria-label={t("visit.workspace.noteActions")}
            className="flex gap-2 border-b px-4 pb-3 [&_button]:h-11 [&_button]:min-w-11"
            data-compact="true"
            role="toolbar"
          >
            {editing ? (
              <>
                <div className="flex-1 [&>button]:w-full">{cancelButton}</div>
                <div className="flex-1 [&>button]:w-full">{saveButton}</div>
              </>
            ) : (
              <>
                {!copyInHeader && (
                  <Button
                    className="flex-1"
                    disabled={!hasText}
                    type="button"
                    onClick={copyNote}
                  >
                    <Copy />
                    {t("visit.workspace.copyNote")}
                  </Button>
                )}
                {exportAction}
                {editable && (
                  <Button
                    className={cn(copyInHeader && "flex-1")}
                    disabled={!hasText || busy}
                    type="button"
                    variant="outline"
                    onClick={startEditing}
                  >
                    <Pencil />
                    {t("visits.templateViewer.buttons.edit")}
                  </Button>
                )}
                {hasMenu && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        aria-label={t("visit.workspace.moreNoteActions")}
                        className="w-11 px-0"
                        data-testid="note-overflow-menu"
                        type="button"
                        variant="outline"
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="max-h-[70dvh] min-w-60 overflow-y-auto **:[[role^=menuitem]]:min-h-11"
                    >
                      {menuItems}
                      {menuItems && onOpenAssistant && (
                        <DropdownMenuSeparator className="first:hidden" />
                      )}
                      {onOpenAssistant && (
                        <DropdownMenuItem
                          className="gap-3"
                          disabled={!hasText}
                          onSelect={onOpenAssistant}
                        >
                          <Sparkles aria-hidden />
                          {t("noteAssistant.openButton")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
          {notices && (
            <div className="space-y-2 border-b px-4 py-3 text-sm empty:hidden">
              {notices}
            </div>
          )}
          {banner}
          {body}
          {feedbackPrompt}
          {/* Room to scroll the value above the review sheet. */}
          {card && <div aria-hidden className="h-[60dvh]" />}
        </div>
        {review.dialog}
        {acceptAllSheet}
        {card}
      </VerifyGateProvider>
    );
  }

  return (
    <VerifyGateProvider value={review.gate}>
      <div className="min-w-0">
        <div className="flex flex-col gap-3 px-4 pb-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:px-6">
          <div className="min-w-0 flex-1">
            {heading}
            {reviewBadge}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0 sm:items-center [&_button]:h-11 [&_button]:min-w-11 lg:[&_button]:h-9 lg:[&_button]:min-w-0">
            {editing ? (
              <>
                <Button
                  className="col-span-2 sm:order-last"
                  disabled={saving || busy}
                  type="button"
                  onClick={() => void save()}
                >
                  {saving ? <LoadingSpinner /> : <Save />}
                  {t("common.save")}
                </Button>
                <Button
                  className="col-span-2"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </>
            ) : (
              <>
                {!copyInHeader && (
                  <Button
                    className="col-span-2 sm:order-last"
                    disabled={!hasText}
                    type="button"
                    onClick={copyNote}
                  >
                    <Copy />
                    {t("visit.workspace.copyNote")}
                  </Button>
                )}
                {exportAction}
              </>
            )}
          </div>
        </div>
        <div
          aria-label={t("visit.workspace.noteActions")}
          className={cn(
            "grid grid-cols-2 gap-2 border-b px-4 pb-3 empty:pb-0 sm:flex sm:flex-wrap sm:items-start sm:px-6 [&_button]:h-11 [&_button]:min-w-11 lg:[&_button]:h-9 lg:[&_button]:min-w-0",
            editing && "hidden",
          )}
          role="toolbar"
        >
          {!editing && (
            <>
              {tools && <div className="col-span-2 sm:contents">{tools}</div>}
              {editable && (
                <Button
                  disabled={!hasText || busy}
                  type="button"
                  variant="outline"
                  onClick={startEditing}
                >
                  <Pencil />
                  {t("visits.templateViewer.buttons.edit")}
                </Button>
              )}
              {onOpenAssistant && (
                <Button
                  disabled={!hasText}
                  type="button"
                  variant="outline"
                  onClick={onOpenAssistant}
                >
                  <Sparkles />
                  {t("noteAssistant.openButton")}
                </Button>
              )}
            </>
          )}
        </div>
        {editing && <div className="border-b" />}
        {banner}
        {body}
        {feedbackPrompt}
      </div>
      {review.dialog}
      {acceptAllSheet}
      {card}
    </VerifyGateProvider>
  );
};
